import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './DashboardCustomization.css';

// Sortable Item Component
function SortableItem({ id, category, isSelected, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`sortable-item ${isSelected ? 'selected' : ''}`}
    >
      <div className="drag-handle">
        <i className="fas fa-grip-vertical"></i>
      </div>
      <div className="item-content">
        <i className={category.icon}></i>
        <span className="item-title">{category.title}</span>
      </div>
      {onToggle && (
        <label className="checkbox-label" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(id)}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="checkmark"></span>
        </label>
      )}
    </div>
  );
}

const DashboardCustomization = ({ isOpen, onClose, onSave, initialConfig }) => {
  const [shortcutCategories, setShortcutCategories] = useState(
    initialConfig?.shortcutCategories || []
  );
  const [metricCards, setMetricCards] = useState(
    initialConfig?.metricCards || []
  );
  const [selectedMetricCards, setSelectedMetricCards] = useState(
    initialConfig?.selectedMetricCards || []
  );
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  // Sync with initialConfig when it changes
  useEffect(() => {
    if (initialConfig?.metricCards) {
      setMetricCards(initialConfig.metricCards);
    }
    if (initialConfig?.selectedMetricCards) {
      setSelectedMetricCards(initialConfig.selectedMetricCards);
    }
    if (initialConfig?.shortcutCategories) {
      setShortcutCategories(initialConfig.shortcutCategories);
    }
  }, [initialConfig]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleShortcutDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setShortcutCategories((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleMetricDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMetricCards((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleMetricCardToggle = (id) => {
    setSelectedMetricCards((prev) => {
      if (prev.includes(id)) {
        // Allow deselection
        return prev.filter((cardId) => cardId !== id);
      } else {
        if (prev.length >= 4) {
          // If 4 cards already selected, replace the first one (FIFO)
          return [...prev.slice(1), id];
        }
        return [...prev, id];
      }
    });
  };

  const handleSave = () => {
    // Validate that exactly 4 metric cards are selected
    if (selectedMetricCards.length !== 4) {
      setShowValidationDialog(true);
      return;
    }
    
    onSave({
      shortcutCategories,
      metricCards,
      selectedMetricCards,
    });
    onClose();
  };

  const handleReset = () => {
    setShowResetDialog(true);
  };

  const confirmReset = () => {
    setShortcutCategories(initialConfig.defaultShortcutCategories);
    setMetricCards(initialConfig.defaultMetricCards);
    setSelectedMetricCards(initialConfig.defaultSelectedMetricCards);
    setShowResetDialog(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Reset Confirmation Dialog */}
      {showResetDialog && (
        <div className="confirmation-dialog-overlay">
          <div className="confirmation-dialog">
            <div className="dialog-icon warning">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3 className="dialog-title">Reset to default layout?</h3>
            <p className="dialog-message">
              This will restore all shortcuts and metric cards to their default settings. Your current customizations will be lost.
            </p>
            <div className="dialog-actions">
              <button className="btn-cancel" onClick={() => setShowResetDialog(false)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={confirmReset}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Dialog */}
      {showValidationDialog && (
        <div className="confirmation-dialog-overlay">
          <div className="confirmation-dialog">
            <div className="dialog-icon error">
              <i className="fas fa-times-circle"></i>
            </div>
            <h3 className="dialog-title">Invalid Selection</h3>
            <p className="dialog-message">
              Please select exactly 4 metric cards before saving. Currently {selectedMetricCards.length} card{selectedMetricCards.length !== 1 ? 's are' : ' is'} selected.
            </p>
            <div className="dialog-actions">
              <button className="btn-confirm" onClick={() => setShowValidationDialog(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="customization-modal-overlay">
        <div className="customization-modal">
        <div className="modal-header">
          <h2>
            <i className="fas fa-cog"></i> Customize Dashboard
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          <div className="customization-section">
            <div className="section-header">
              <h3>
                <i className="fas fa-th-large"></i> Shortcut Categories Order
              </h3>
              <p className="section-description">
                Drag to reorder categories based on your priority
              </p>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleShortcutDragEnd}
            >
              <SortableContext
                items={shortcutCategories.map((cat) => cat.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="sortable-list">
                  {shortcutCategories.map((category) => (
                    <SortableItem
                      key={category.id}
                      id={category.id}
                      category={category}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="customization-section">
            <div className="section-header">
              <h3>
                <i className="fas fa-chart-bar"></i> Key Metrics (Top Banner)
              </h3>
              <p className="section-description">
                Select exactly 4 metrics to display in the banner ({selectedMetricCards.length}/4 selected)
              </p>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleMetricDragEnd}
            >
              <SortableContext
                items={metricCards.map((card) => card.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="sortable-list">
                  {metricCards.map((card) => (
                    <SortableItem
                      key={card.id}
                      id={card.id}
                      category={card}
                      isSelected={selectedMetricCards.includes(card.id)}
                      onToggle={handleMetricCardToggle}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={handleReset}>
            <i className="fas fa-undo"></i> Reset to Default
          </button>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={selectedMetricCards.length !== 4}
              style={{ opacity: selectedMetricCards.length !== 4 ? 0.5 : 1 }}
            >
              <i className="fas fa-save"></i> Save Changes
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default DashboardCustomization;
